'use client'

import { useEffect, useState } from 'react'

const TABLET_BREAKPOINT = 768
const MOBILE_BREAKPOINT = 480

const mobileDeviceRegex =
  /Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i

// Pure device detection
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false

  // First check, best way for modern browsers
  const userAgentMobile = (navigator as any)?.userAgentData?.mobile
  if (userAgentMobile) {
    return true
  }

  // Fallback for older browsers
  return mobileDeviceRegex.test(navigator.userAgent || '')
}

// Pure touch capability detection
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (window.matchMedia?.('(pointer: coarse)').matches ?? false)
  )
}

export function isMobileScreen() {
  return isMobileDevice() || isTouchDevice()
}
function useScreenNarrowerThanBreakpoint(pixels: number): boolean {
  const query = `(max-width: ${pixels}px)`

  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia(query)
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches)

    setMatches(mediaQuery.matches)
    mediaQuery.addEventListener('change', handler)

    return () => mediaQuery.removeEventListener?.('change', handler)
  }, [query])

  return matches
}

export function useIsMobileSize() {
  return useScreenNarrowerThanBreakpoint(MOBILE_BREAKPOINT)
}

export function useIsTabletOrMobileSize() {
  return useScreenNarrowerThanBreakpoint(TABLET_BREAKPOINT)
}

export default function useIsMobileScreen() {
  const isMobileSize = useIsMobileSize()

  const [isMobile, setIsMobile] = useState(false)
  const [isTouch, setIsTouch] = useState(false)

  useEffect(() => {
    setIsTouch(isTouchDevice())
    setIsMobile(isMobileDevice())
  }, [])

  return isMobileSize || isMobile || isTouch
}

export function useIsTabletOrMobileScreen() {
  const isMobileSize = useIsTabletOrMobileSize()

  const [isMobile, setIsMobile] = useState(false)
  const [isTouch, setIsTouch] = useState(false)

  useEffect(() => {
    setIsTouch(isTouchDevice())
    setIsMobile(isMobileDevice())
  }, [])

  return isMobileSize || isMobile || isTouch
}
